/**
 * Query parameters from the auth callback that must not outlive it.
 *
 * `code` is the authorization code and `token` is a signup token that stands in for a session
 * (`connect-callback.tsx` skips the auth headers when it is present); `state`/`session_state` bind
 * the round trip to this browser. The code is single-use and already spent by the time we clean up,
 * but these are all credentials in a url that lands in browser history and in anything reading
 * `window.location.href` — Sentry attaches it to every event it sends. `error_description` is the
 * provider's free text, which nothing displays (see `getOpenIdErrorReason`), so there is no reason
 * to keep it either.
 *
 * `error` deliberately stays, so that reloading the failure screen still explains what went wrong.
 * The value is not narrowed here — an unrecognised one is left in the url as the provider sent it,
 * and is only resolved to a message we own at render time.
 */
const CALLBACK_PARAMS_TO_DROP = ['code', 'token', 'state', 'session_state', 'error_description'];
/**
 * Rebuilds the current url without the parameters the OIDC provider redirected back with.
 *
 * Everything before the hash is dropped outright — the app never keeps anything it needs there —
 * while inside the hash only the callback's own parameters are removed. Takes the two parts as
 * arguments rather than reading `window.location` so it can be tested: jsdom makes `window.location`
 * non-configurable.
 *
 * The provider redirects to `/?code=123&state=abc`, and `application.tsx` copies those parameters
 * into the hash so the hash router can read them. Dropping everything before the hash — which is
 * all the cleanup used to do — therefore left the authorization code behind inside the hash.
 *
 * @param pathname The current `window.location.pathname`
 * @param hash The current `window.location.hash`, including its own query string
 * @returns The url to replace the current history entry with
 *
 * @example
 * ```ts
 * buildUrlWithoutOpenIdParameters('/', '#/openid/redirect?code=123&email=a@b.c');
 * // "/#/openid/redirect?email=a%40b.c"
 * ```
 */
export const buildUrlWithoutOpenIdParameters = (pathname, hash) => {
    // `indexOf` rather than `split('?')`, which would discard everything after a second `?`
    const queryIndex = hash.indexOf('?');
    if (queryIndex === -1) {
        return `${pathname}${hash}`;
    }
    const hashPath = hash.slice(0, queryIndex);
    const remaining = new URLSearchParams(hash.slice(queryIndex + 1));
    CALLBACK_PARAMS_TO_DROP.forEach(parameter => remaining.delete(parameter));
    const remainingQueryString = remaining.toString();
    return `${pathname}${hashPath}${remainingQueryString ? `?${remainingQueryString}` : ''}`;
};
